import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { X, Maximize2 } from 'lucide-react';
interface MarkdownProps {
  children: string;
  className?: string;
  /** Enable click-to-fullscreen on images */
  expandableImages?: boolean;
  /**
   * Controls how images are rendered:
   * - 'natural': Full width, natural height (no cropping, default)
   * - 'constrained': Full width, max height of 600px, object-contain (no crop, fits tall images)
   * - 'cover-landscape': Forced 16:9 aspect ratio with object-cover (crops tall images)
   * - 'cover-square': Forced 1:1 aspect ratio with object-cover
   */
  imageLayout?: 'natural' | 'constrained' | 'cover-landscape' | 'cover-square';
}
export function Markdown(t0) {
  const $ = _c(16);
  const {
    children,
    className: t1,
    expandableImages: t2,
    imageLayout: t3
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const expandableImages = t2 === undefined ? true : t2;
  const imageLayout = t3 === undefined ? "constrained" : t3;
  const [fullscreenSrc, setFullscreenSrc] = useState(null);
  let t4;
  if ($[0] !== fullscreenSrc) {
    t4 = fullscreenSrc && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 cursor-pointer" onClick={() => setFullscreenSrc(null)}><button className="absolute top-4 right-4 text-white hover:text-gray-300 cursor-pointer" onClick={() => setFullscreenSrc(null)} aria-label="Close fullscreen image"><X className="w-8 h-8" /></button><img src={fullscreenSrc} alt="Fullscreen view" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={_temp} /></div>;
    $[0] = fullscreenSrc;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = `markdown-content min-w-0 max-w-full [overflow-wrap:anywhere] ${className}`;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [remarkGfm, remarkBreaks];
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [[rehypeExternalLinks, {
      target: "_blank",
      rel: ["noopener", "noreferrer"]
    }]];
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== expandableImages || $[5] !== imageLayout) {
    t8 = {
      h1: _temp2,
      h2: _temp3,
      h3: _temp4,
      h4: _temp5,
      p: _temp6,
      a: _temp7,
      ul: _temp8,
      ol: _temp9,
      li: _temp10,
      strong: _temp11,
      em: _temp12,
      blockquote: _temp13,
      code: _temp14,
      pre: _temp15,
      table: _temp16,
      thead: _temp17,
      tbody: _temp18,
      tr: _temp19,
      th: _temp20,
      td: _temp21,
      img: t9 => {
        const {
          src,
          alt
        } = t9;
        const imgSrc = typeof src === "string" ? src : "";
        const imageClasses = (() => {
          switch (imageLayout) {
            case "constrained":
              {
                return "w-full max-h-[600px] object-contain rounded-lg my-4";
              }
            case "cover-landscape":
              {
                return "w-full aspect-video object-cover rounded-lg my-4";
              }
            case "cover-square":
              {
                return "w-full aspect-square object-cover rounded-lg my-4";
              }
            case "natural":
            default:
              {
                return "w-full h-auto rounded-lg my-4";
              }
          }
        })();
        const image = <img src={imgSrc} alt={alt || ""} className={`${imageClasses} ${expandableImages ? "cursor-pointer" : ""}`} loading="lazy" />;
        if (expandableImages && imgSrc) {
          return <span className="relative block cursor-pointer group" onClick={() => setFullscreenSrc(imgSrc)} role="button" tabIndex={0} onKeyDown={e_0 => {
            if (e_0.key === "Enter" || e_0.key === " ") {
              setFullscreenSrc(imgSrc);
            }
          }}>{image}<span className="absolute top-2 right-2 bg-black/50 text-white rounded p-1 opacity-60 group-hover:opacity-100 transition-opacity"><Maximize2 className="w-4 h-4" /></span></span>;
        }
        return image;
      },
      hr: _temp22,
      br: _temp23
    };
    $[4] = expandableImages;
    $[5] = imageLayout;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== children || $[8] !== t8) {
    t9 = <ReactMarkdown remarkPlugins={t6} rehypePlugins={t7} components={t8}>{children}</ReactMarkdown>;
    $[7] = children;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t5 || $[11] !== t9) {
    t10 = <div className={t5}>{t9}</div>;
    $[10] = t5;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t4) {
    t11 = <>{t4}{t10}</>;
    $[13] = t10;
    $[14] = t4;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
}
function _temp23() {
  return <br className="block content-[''] mb-2" />;
}
function _temp22() {
  return <hr className="my-6 border-t border-gray-200" />;
}
function _temp21(t0) {
  const {
    children: children_19
  } = t0;
  return <td className="border border-gray-200 px-4 py-2 text-gray-700">{children_19}</td>;
}
function _temp20(t0) {
  const {
    children: children_18
  } = t0;
  return <th className="border border-gray-200 px-4 py-2 text-left font-semibold text-gray-900">{children_18}</th>;
}
function _temp19(t0) {
  const {
    children: children_17
  } = t0;
  return <tr className="border-b border-gray-200">{children_17}</tr>;
}
function _temp18(t0) {
  const {
    children: children_16
  } = t0;
  return <tbody>{children_16}</tbody>;
}
function _temp17(t0) {
  const {
    children: children_15
  } = t0;
  return <thead className="bg-gray-50">{children_15}</thead>;
}
function _temp16(t0) {
  const {
    children: children_14
  } = t0;
  return <div className="overflow-x-auto mb-4"><table className="min-w-full border-collapse border border-gray-200 text-sm">{children_14}</table></div>;
}
function _temp15(t0) {
  const {
    children: children_13
  } = t0;
  return <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg overflow-x-auto mb-4">{children_13}</pre>;
}
function _temp14(t0) {
  const {
    children: children_12
  } = t0;
  return <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">{children_12}</code>;
}
function _temp13(t0) {
  const {
    children: children_11
  } = t0;
  return <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4">{children_11}</blockquote>;
}
function _temp12(t0) {
  const {
    children: children_10
  } = t0;
  return <em className="italic">{children_10}</em>;
}
function _temp11(t0) {
  const {
    children: children_9
  } = t0;
  return <strong className="font-semibold text-gray-900">{children_9}</strong>;
}
function _temp10(t0) {
  const {
    children: children_8
  } = t0;
  return <li className="text-gray-700 pl-2">{children_8}</li>;
}
function _temp9(t0) {
  const {
    children: children_7
  } = t0;
  return <ol className="list-decimal list-outside ml-6 mb-4 space-y-0 text-gray-700">{children_7}</ol>;
}
function _temp8(t0) {
  const {
    children: children_6
  } = t0;
  return <ul className="list-disc list-outside ml-6 mb-4 space-y-0 text-gray-700">{children_6}</ul>;
}
function _temp7(t0) {
  const {
    href,
    children: children_5,
    target,
    rel,
    ...rest
  } = t0;
  if (href && href.startsWith("/")) {
    return <Link href={href} className="text-primary-600 hover:text-primary-700 underline cursor-pointer">{children_5}</Link>;
  }
  return <a href={href} className="text-primary-600 hover:text-primary-700 underline cursor-pointer" target={target} rel={rel} {...rest}>{children_5}</a>;
}
function _temp6(t0) {
  const {
    children: children_4
  } = t0;
  return <p className="mb-4 min-w-0 max-w-full text-gray-700 [overflow-wrap:anywhere]">{children_4}</p>;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <h4 className="text-lg font-semibold text-gray-900 mt-3 mb-2">{children_3}</h4>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">{children_2}</h3>;
}
function _temp3(t0) {
  const {
    children: children_1
  } = t0;
  return <h2 className="text-2xl font-bold text-gray-900 mt-5 mb-3">{children_1}</h2>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <h1 className="text-3xl font-bold text-gray-900 mt-6 mb-4">{children_0}</h1>;
}
function _temp(e) {
  return e.stopPropagation();
}